import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { ListChecks } from "lucide-react";
import Modal from "@/components/Modal";
import type { SponsorItem } from "@/services/api/sponsor/getSponsors";

interface StudentSponsorCardProps {
  sponsor: SponsorItem;
  /** `personal` / `society` when the student already redeemed this sponsor. */
  redeemedType?: "personal" | "society" | null;
  onRedeemClick: (sponsor: SponsorItem) => void;
}

/**
 * Sponsor card of the redeem centre (port of the RN `AnimatedSponsorCard`).
 * Pressing the card lists the sponsor tasks, the trailing action redeems the
 * sponsor or shows which redemption type is already in use.
 *
 * The card carries no state at all and the modal owns the whole flow.
 *
 * No money is ever shown on the card: the sponsor unit price stays a server-side
 * input to the quantity cap.
 *
 * The server re-validates participants and quantity on every redemption.
 */
const StudentSponsorCard: React.FC<StudentSponsorCardProps> = ({
  sponsor,
  redeemedType,
  onRedeemClick,
}) => {
  const { t } = useTranslation();
  const [tasksVisible, setTasksVisible] = useState(false);

  return (
    <>
      {/* Row 1 is the fixed 1.8:1 image box, row 2 absorbs the leftover height
          so cards in a grid row stay equally tall and the action stays pinned to
          the bottom. Keep this a grid, NOT a flex column: a flex item with
          `aspect-ratio` and an auto height collapses the 1.8:1 box to 1:1. */}
      <div className="grid w-full grid-rows-[auto_1fr] overflow-hidden rounded-2xl border border-gray-100 bg-white text-left shadow-sm transition hover:shadow-md">
        <button
          type="button"
          onClick={() => setTasksVisible(true)}
          className="relative block aspect-[1.8/1] w-full bg-gray-100"
        >
          {sponsor.image_url ? (
            <img
              src={sponsor.image_url}
              alt={sponsor.name}
              className="h-full w-full object-contain"
            />
          ) : null}
          <span className="absolute right-3 top-3 rounded-lg bg-[#ffa217] px-2.5 py-1 text-[13px] font-bold text-white shadow-md">
            {t("Sponsor Tasks")}
          </span>
        </button>

        <div className="flex items-start gap-3 px-4 py-3.5">
          <button
            type="button"
            onClick={() => setTasksVisible(true)}
            className="min-w-0 flex-1 text-left"
          >
            {/* Product names are long and have no spaces to break on, so the
                name wraps over as many lines as it needs instead of clamping. */}
            <p className="break-words text-base font-semibold leading-snug text-gray-900">
              {sponsor.name || t("Room name")}
            </p>
            {sponsor.description?.trim() ? (
              <p className="mt-0.5 line-clamp-3 text-[13px] text-gray-500">
                {sponsor.description}
              </p>
            ) : null}
            {sponsor.category && (
              <span className="mt-1.5 inline-block rounded-lg bg-gray-100 px-2 py-0.5 text-[11px] font-semibold text-gray-600">
                {sponsor.category}
              </span>
            )}
          </button>
          <div className="flex shrink-0 flex-col items-end gap-2 self-end">
            {redeemedType ? (
              <span className="rounded-lg bg-[#4CAF50] px-3 py-1.5 text-[13px] font-bold text-white">
                {`${t("Redeemed")} (${
                  redeemedType === "personal" ? t("Personal") : t("Society")
                })`}
              </span>
            ) : (
              <button
                type="button"
                onClick={() => onRedeemClick(sponsor)}
                className="rounded-lg border border-[#ffa217] px-3 py-1.5 text-[15px] font-bold text-[#ffa217] transition hover:bg-[#ffa217]/10"
              >
                {t("Redeem now")}
              </button>
            )}
          </div>
        </div>

      </div>

      <Modal visible={tasksVisible} onClose={() => setTasksVisible(false)}>
        <div className="rounded-2xl bg-white p-5 shadow-lg">
          <div className="mb-3 flex items-center gap-2">
            <ListChecks size={18} className="text-[#ffa217]" />
            <h3 className="text-base font-bold text-gray-900">
              {t("All Tasks")}
            </h3>
          </div>
          {sponsor.tasks.length > 0 ? (
            <ul className="max-h-72 space-y-2 overflow-y-auto pr-1">
              {sponsor.tasks.map((task) => (
                <li
                  key={task.id}
                  className="rounded-xl bg-gray-50 px-3 py-2 text-sm text-gray-700"
                >
                  {task.title || t("No tasks")}
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-sm text-gray-500">{t("No tasks")}</p>
          )}
          <button
            type="button"
            onClick={() => setTasksVisible(false)}
            className="mt-4 w-full rounded-xl bg-[#ffa217] py-2.5 text-sm font-bold text-white"
          >
            {t("OK")}
          </button>
        </div>
      </Modal>
    </>
  );
};

export default StudentSponsorCard;
